import React, { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    TextField,
    Grid,
    FormControlLabel,
    Switch,
    Stack,
    Card,
    CardActions,
    CardContent,
    useMediaQuery,
    Slide,
    TableContainer,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    CircularProgress,
    ThemeProvider,
    Tooltip,
    IconButton,
    Menu,
    MenuItem
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useAtom } from "jotai";
import { useTheme } from "@emotion/react";
import {
    selectedCourseBookingAtom,
    selectedCourseBookingManifestAtom
} from "../../atoms/course";
import { MobileBookingsTheme } from "../../styles/MobileBookingsTheme";
import { useQuery } from "@tanstack/react-query";
import { execReadBySortkey } from "../../modules/DBService";
import { sessionManifest } from "../../queries/CustomQueries";
import { postApi } from "../../modules/AmplifyServices";
import { use } from "react";

const MobileCourseManifestItemCard = ({ item, onMenuClick }) => {
    return (
        <Card>
            <CardContent
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start"
                }}
            >
                <div>
                    <Typography variant="body2">{`${item.client.user.firstname} ${item.client.user.lastname}`}</Typography>
                    <Typography variant="body2">{item.orderNo}</Typography>
                    {item?.note && (
                        <Typography
                            variant="body2"
                            sx={{ fontStyle: "italic", paddingTop: "18px" }}
                        >
                            {item.note}
                        </Typography>
                    )}
                </div>
                <IconButton size="small" onClick={onMenuClick}>
                    <MoreVertIcon fontSize="small" />
                </IconButton>
            </CardContent>
        </Card>
    );
};

const CourseManifestModal = ({ open, onClose, refreshTable }) => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("md"), {
        defaultMatches: true
    });
    const [selectedCourseBooking, setSelectedCourseBooking] = useAtom(
        selectedCourseBookingAtom
    );
    const [manifest, setManifest] = useAtom(selectedCourseBookingManifestAtom);

    const [menuAnchor, setMenuAnchor] = useState(null);
    const [menuRowIndex, setMenuRowIndex] = useState(null);
    const [menuRowClientName, setMenuRowClientName] = useState("");
    const [confirmOpen, setConfirmOpen] = useState(false);

    function getManifestTitle() {
        if (selectedCourseBooking) {
            const {
                servicetype: { name: courseName },
                startdate,
                courseInfo: { sessionNo, numberOfSessions }
            } = selectedCourseBooking;
            return (
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px"
                    }}
                >
                    <Typography variant="h3" lineHeight={1.1}>
                        {`${courseName} – ${new Date(
                            startdate
                        ).toLocaleDateString("en-US", {
                            weekday: "short",
                            month: "short",
                            day: "2-digit",
                            year: "numeric"
                        })}`}
                    </Typography>
                    <Typography variant="h3" lineHeight={1.1}>
                        {`Class ${sessionNo} of ${numberOfSessions}`}
                    </Typography>
                </div>
            );
        }
    }

    const { refetch, isFetching } = useQuery({
        queryKey: ["get-course-manifest", selectedCourseBooking?.id],
        queryFn: async () => {
            try {
                let { items, error } = await execReadBySortkey({
                    opname: "registrationBySession",
                    op: sessionManifest,
                    id: {
                        sessionId: selectedCourseBooking?.courseInfo.sessionId
                    },
                    limit: 500
                });
                if (error) throw error;
                items?.forEach(({ booking }) => {
                    try {
                        booking.Notes =
                            typeof booking.Notes === "string" &&
                            booking.Notes.length > 0
                                ? JSON.parse(booking.Notes)
                                : booking.Notes
                                  ? booking.Notes
                                  : "";
                    } catch (e) {
                        booking.Notes = "";
                    }

                    booking.note = booking.Notes ? booking.Notes[0].notes : "";
                    if (!booking.note && booking.order.Notes) {
                        booking.note = JSON.parse(booking.order.Notes)[0].notes;
                    }
                });
                return items;
            } catch (e) {
                console.log(e);
            }
        },
        onSuccess: (registrations) => {
            setManifest(registrations);
        }
    });

    const handleMenuOpen = (e, index) => {
        setMenuAnchor(e.currentTarget);
        setMenuRowIndex(index);
        setMenuRowClientName(
            `${manifest[index].client.user.firstname} ${manifest[index].client.user.lastname}`
        );
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
        setMenuRowIndex(null);
        setMenuRowClientName("");
    };

    const handleRemoveClick = () => {
        setMenuAnchor(null);
        setConfirmOpen(true);
    };

    const handleConfirmRemove = async () => {
        const registration = manifest[menuRowIndex];
        setConfirmOpen(false);
        setMenuRowIndex(null);
        setMenuRowClientName("");
        await handleRemoveRegistration(registration);
        setManifest((prev) => prev.filter((_, i) => i !== menuRowIndex));
        if (refreshTable) {
            refreshTable();
        }
    };

    const handleConfirmCancel = () => {
        setConfirmOpen(false);
        setMenuRowIndex(null);
        setMenuRowClientName("");
    };

    const handleRemoveRegistration = async (registration) => {
        try {
            const { serviceTypeId, clientId, sequenceId } = registration;
            const { success } = await postApi(
                "serviceManagement",
                `/service/${serviceTypeId}/sequence/${sequenceId}/registration/remove`,
                {
                    body: {
                        clientId
                    }
                }
            );
            console.log("success", success);
        } catch (error) {
            console.log("Error while removing registration:", error);
        }
    };

    const lastRowIndex = manifest.length - 1;

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                fullWidth
                maxWidth="lg"
                fullScreen={!isDesktop}
            >
                <DialogTitle
                    sx={{
                        paddingBottom: "36px"
                    }}
                >
                    {getManifestTitle()}
                </DialogTitle>

                <DialogContent sx={{ minHeight: "200px" }}>
                    {isDesktop ? (
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell variant="head">
                                            Client
                                        </TableCell>
                                        <TableCell variant="head">
                                            Booking notes
                                        </TableCell>
                                        <TableCell variant="head">
                                            Order#
                                        </TableCell>
                                        <TableCell
                                            variant="head"
                                            sx={{ width: "48px", padding: "0" }}
                                        />
                                    </TableRow>
                                </TableHead>
                                {isFetching && !manifest.length ? (
                                    <TableBody>
                                        <TableRow>
                                            <TableCell
                                                colSpan={4}
                                                sx={{
                                                    textAlign: "center",
                                                    padding: "32px !important",
                                                    borderBottom: "none"
                                                }}
                                            >
                                                <CircularProgress />
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                ) : (
                                    <TableBody>
                                        {manifest.map(
                                            (
                                                {
                                                    client: {
                                                        user: {
                                                            firstname: fn,
                                                            lastname: ln,
                                                            emailaddress,
                                                            mobilephone
                                                        }
                                                    },
                                                    booking: { note },
                                                    orderNo
                                                },
                                                index
                                            ) => (
                                                <TableRow key={index}>
                                                    <TableCell
                                                        sx={{
                                                            ...(index ===
                                                                lastRowIndex && {
                                                                borderBottom:
                                                                    "none"
                                                            })
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                flexDirection:
                                                                    "column"
                                                            }}
                                                        >
                                                            <span>
                                                                <Typography
                                                                    sx={{
                                                                        fontWeight: 500,
                                                                        color: "black"
                                                                    }}
                                                                    variant="body1"
                                                                >
                                                                    {`${fn} ${ln}`}
                                                                </Typography>
                                                            </span>
                                                            <span>
                                                                <Typography variant="body1">
                                                                    {
                                                                        emailaddress
                                                                    }
                                                                </Typography>
                                                            </span>
                                                            <span>
                                                                <Typography variant="body1">
                                                                    {
                                                                        mobilephone
                                                                    }
                                                                </Typography>
                                                            </span>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            position:
                                                                "relative",
                                                            overflow: "hidden",
                                                            maxWidth: "250px",
                                                            ...(index ===
                                                                lastRowIndex && {
                                                                borderBottom:
                                                                    "none"
                                                            })
                                                        }}
                                                    >
                                                        {note && (
                                                            <Tooltip
                                                                title={
                                                                    note.length >
                                                                    95
                                                                        ? note
                                                                        : ""
                                                                }
                                                                arrow
                                                            >
                                                                <Typography
                                                                    sx={{
                                                                        display:
                                                                            "block",
                                                                        fontStyle:
                                                                            "italic",
                                                                        whiteSpace:
                                                                            "pre-wrap",
                                                                        overflow:
                                                                            "hidden",
                                                                        textOverflow:
                                                                            "ellipsis",
                                                                        lineHeight:
                                                                            "16px",
                                                                        maxHeight:
                                                                            "80px",
                                                                        wordBreak:
                                                                            "break-word",
                                                                        maxWidth:
                                                                            "250px"
                                                                    }}
                                                                >
                                                                    {note.length >
                                                                    95
                                                                        ? `"${note.slice(
                                                                              0,
                                                                              95
                                                                          )}..."`
                                                                        : `"${note}"`}
                                                                </Typography>
                                                            </Tooltip>
                                                        )}
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            minWidth: "120px",
                                                            maxWidth: "120px",
                                                            ...(index ===
                                                                lastRowIndex && {
                                                                borderBottom:
                                                                    "none"
                                                            })
                                                        }}
                                                    >
                                                        <Typography variant="body1">{`${orderNo}`}</Typography>
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            width: "48px",
                                                            padding: "0 8px",
                                                            ...(index ===
                                                                lastRowIndex && {
                                                                borderBottom:
                                                                    "none"
                                                            })
                                                        }}
                                                    >
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) =>
                                                                handleMenuOpen(
                                                                    e,
                                                                    index
                                                                )
                                                            }
                                                        >
                                                            <MoreVertIcon fontSize="small" />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        )}
                                    </TableBody>
                                )}
                            </Table>
                        </TableContainer>
                    ) : (
                        <ThemeProvider theme={MobileBookingsTheme(theme)}>
                            {isFetching && !manifest.length ? (
                                <Grid container justifyContent={"center"} p={4}>
                                    <CircularProgress />
                                </Grid>
                            ) : (
                                <Stack>
                                    {manifest.map((row, index) => (
                                        <MobileCourseManifestItemCard
                                            key={index}
                                            item={row}
                                            onMenuClick={(e) =>
                                                handleMenuOpen(e, index)
                                            }
                                        />
                                    ))}
                                </Stack>
                            )}
                        </ThemeProvider>
                    )}
                </DialogContent>

                <DialogActions
                    sx={{ display: "flex", justifyContent: "flex-end" }}
                >
                    <Button
                        onClick={() => {
                            setSelectedCourseBooking();
                            setManifest([]);
                            onClose();
                        }}
                        variant="contained"
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={handleRemoveClick}>Remove</MenuItem>
            </Menu>

            <Dialog
                open={confirmOpen}
                onClose={handleConfirmCancel}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Remove Registration</DialogTitle>
                <DialogContent>
                    {menuRowClientName ? (
                        <Typography>
                            Are you sure you want to remove {menuRowClientName}{" "}
                            from this course?
                        </Typography>
                    ) : (
                        <Typography>
                            Are you sure you want to remove this registration?
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleConfirmCancel}>Cancel</Button>
                    <Button onClick={handleConfirmRemove} variant="contained">
                        Remove
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default CourseManifestModal;
